/**
 * FlowLayout - Main layout for journey-guided entity flow
 * Two-panel layout: IntakePanel (left) + Main content (center)
 * Flow Coach is now in the DetailSidebar tabs on the right
 */

import { useEffect, useState, useCallback } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { useFlowWorkflow } from '@/hooks/useFlowWorkflow'
import { useEntityCreation } from '@/hooks/useEntityTree'
import { entityTreeApi } from '@/lib/api/client'
import { IntakePanel } from './IntakePanel'
import { PhaseNavigation } from './PhaseNavigation'
import { EntityCardGrid } from './EntityCardGrid'
import { type AISuggestion } from './EntityCard'
import { CreateInstanceDialog } from '@/components/shared/CreateInstanceDialog'
import { Loader2 } from 'lucide-react'
import type { EntityDomain } from '@ispot/shared'

export function FlowLayout() {
  const { accountId, projectId } = useSessionStore()
  const { fetchJourney } = useJourneyStore()

  const {
    phases,
    currentPhase,
    currentPhaseIndex,
    totalPhases,
    phaseProgress,
    phaseEntityCards,
    isLoading,
    setCurrentPhase,
    goToNextPhase,
    canGoNext,
  } = useFlowWorkflow()

  // Entity creation dialog state (null = closed, object = open with class info)
  const [createForClass, setCreateForClass] = useState<{
    classUri: string
    className: string
    domain: string
    color: string
  } | null>(null)

  // AI Suggestions state
  const [suggestionsByClass, setSuggestionsByClass] = useState<Record<string, AISuggestion[]>>({})
  const [loadingClasses, setLoadingClasses] = useState<Set<string>>(new Set())

  // Check if journey has started (any entities created)
  const hasStarted = phaseEntityCards.some(card => card.currentCount > 0)

  // Entity creation hook for accepting suggestions
  const { createEntityAsync } = useEntityCreation()

  // Handler to get AI suggestions for a class
  const handleAISuggestionsClick = useCallback(async (
    classUri: string,
    className: string,
    domain: EntityDomain
  ) => {
    if (!accountId || !projectId) return

    // Mark as loading
    setLoadingClasses(prev => new Set(prev).add(classUri))

    try {
      // Build full class URI if needed (API expects full URI format)
      const fullClassUri = classUri.includes('/')
        ? classUri
        : `http://www.intellispot.com/ontology/${domain}#${className}`

      // Get phase context for better suggestions
      const phaseContext = currentPhase ? {
        phaseName: currentPhase.name,
        phaseDescription: currentPhase.description,
      } : undefined

      const response = await entityTreeApi.getSuggestions(
        accountId,
        projectId,
        fullClassUri,
        phaseContext
      )

      // Transform API suggestions to component format
      const suggestions: AISuggestion[] = response.suggestions.map(s => ({
        id: s.id,
        label: s.name,
        reason: s.reason,
        confidence: s.confidence,
      }))

      setSuggestionsByClass(prev => ({
        ...prev,
        [classUri]: suggestions,
      }))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get suggestions:', error)
    } finally {
      setLoadingClasses(prev => {
        const next = new Set(prev)
        next.delete(classUri)
        return next
      })
    }
  }, [accountId, projectId, currentPhase])

  // Handler to accept a suggestion (create the entity)
  const handleAcceptSuggestion = useCallback(async (
    suggestion: AISuggestion,
    classUri: string
  ) => {
    // Find the card to get the class info
    const card = phaseEntityCards.find(c => c.classUri === classUri)
    if (!card) {
      // eslint-disable-next-line no-console
      console.warn('Card not found for classUri:', classUri, 'Available:', phaseEntityCards.map(c => c.classUri))
      return
    }

    try {
      // Create the entity - API expects class name (e.g., "CustomerSegment"), not full URI
      const result = await createEntityAsync({
        entityType: card.className,
        label: suggestion.label,
        description: suggestion.reason,
      })

      // eslint-disable-next-line no-console
      console.log('Entity created:', result)

      // Remove the accepted suggestion from the list
      setSuggestionsByClass(prev => ({
        ...prev,
        [classUri]: (prev[classUri] || []).filter(s => s.id !== suggestion.id),
      }))

      // Refresh journey to update counts
      if (accountId && projectId) {
        await fetchJourney(accountId, projectId)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create entity from suggestion:', error)
    }
  }, [phaseEntityCards, createEntityAsync, accountId, projectId, fetchJourney])

  // Handler to open create entity dialog
  const handleAddEntity = useCallback((classUri: string) => {
    // Find the card info for this classUri
    const card = phaseEntityCards.find(c => c.classUri === classUri)
    if (card) {
      setCreateForClass({
        classUri: card.classUri,
        className: card.className,
        domain: card.domain,
        color: currentPhase?.color || '#5c8a6b',
      })
    }
  }, [phaseEntityCards, currentPhase])

  // Handle dialog close
  const handleDialogClose = useCallback(() => {
    setCreateForClass(null)
  }, [])

  // Fetch journey data on mount
  useEffect(() => {
    if (accountId && projectId) {
      fetchJourney(accountId, projectId)
    }
  }, [accountId, projectId, fetchJourney])

  // Loading state
  if (isLoading && phases.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-warm-50">
        <div className="flex items-center gap-3 text-text-muted">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading journey...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex bg-warm-50">
      {/* Left Panel - Intake (hidden once journey has started) */}
      {!hasStarted && (
        <aside className="w-80 flex-shrink-0 bg-white border-r border-border-subtle overflow-y-auto">
          <IntakePanel />
        </aside>
      )}

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Phase Navigation */}
        <PhaseNavigation
          phases={phases}
          currentPhaseId={currentPhase?.id || ''}
          phaseProgress={phaseProgress}
          onPhaseSelect={setCurrentPhase}
        />

        {/* Phase Content */}
        <div className="flex-1 overflow-y-auto">
          {currentPhase && (
            <div className="p-8">
              {/* Phase Header */}
              <div className="flex items-start gap-4 mb-8">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-white"
                  style={{ background: `linear-gradient(135deg, ${currentPhase.color || '#5c8a6b'} 0%, ${adjustColor(currentPhase.color || '#5c8a6b', -20)} 100%)` }}
                >
                  <PhaseIcon domain={currentPhase.domain} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold" style={{ color: currentPhase.color || '#5c8a6b' }}>
                    Phase {currentPhaseIndex + 1}: {currentPhase.name}
                  </h2>
                  <h3 className="text-base font-medium text-text mt-0.5">
                    {currentPhase.objective || 'Complete this phase'}
                  </h3>
                  <p className="text-sm text-text-secondary mt-1 max-w-2xl">
                    {currentPhase.description}
                  </p>
                </div>
              </div>

              {/* Entity Cards Grid */}
              <EntityCardGrid
                cards={phaseEntityCards}
                onAddEntity={handleAddEntity}
                onAISuggestionsClick={handleAISuggestionsClick}
                suggestionsByClass={suggestionsByClass}
                loadingClasses={loadingClasses}
                onAcceptSuggestion={handleAcceptSuggestion}
              />
            </div>
          )}
        </div>

        {/* Create Entity Dialog */}
        {createForClass && (
          <CreateInstanceDialog
            classUri={createForClass.classUri}
            classLabel={createForClass.className}
            color={createForClass.color}
            onClose={handleDialogClose}
          />
        )}

        {/* Phase Footer */}
        <footer className="flex-shrink-0 px-8 py-4 bg-white border-t border-border-subtle">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted">
              Phase {currentPhaseIndex + 1} of {totalPhases}
            </span>
            {canGoNext && (
              <button
                onClick={goToNextPhase}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors"
                style={{ backgroundColor: 'var(--color-market)' }}
              >
                {phases[currentPhaseIndex + 1]?.name || 'Next Phase'}
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </footer>
      </main>
    </div>
  )
}

// Helper to darken/lighten color
function adjustColor(color: string, amount: number): string {
  const hex = color.replace('#', '')
  const r = Math.max(0, Math.min(255, parseInt(hex.slice(0, 2), 16) + amount))
  const g = Math.max(0, Math.min(255, parseInt(hex.slice(2, 4), 16) + amount))
  const b = Math.max(0, Math.min(255, parseInt(hex.slice(4, 6), 16) + amount))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

// Phase icon based on domain
function PhaseIcon({ domain }: { domain: string }) {
  if (domain === 'market') {
    return (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    )
  }
  if (domain === 'solution') {
    return (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    )
  }
  // business
  return (
    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  )
}
